import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type WheelEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import StyleConstants from '../../../core/styles.constants';
import WorkplaceOccupantTooltip from './workplace-occupant-tooltip.component';
import type { WorkplaceOccupancyMap } from '../hooks/workplace-occupants.hooks';
import type FloorPlan from '../../dashboard/models/floor-plan';
import LayerBackdrop from '../../floorplans/components/layer-backdrop.component';
import DeskShape from '../../floorplans/components/desk-shape.component';
import AnnotationLabel from '../../floorplans/components/annotation-label.component';
import { useMdiIcons } from '../../floorplans/hooks/use-mdi-icons';
import { useViewport } from '../../floorplans/hooks/use-viewport';
import { normalizeFloorPlan } from '../../floorplans/utils/normalize-floorplan';
import { computeBoundingBox, createProjection, toScreen } from '../../floorplans/utils/projection';
import medicWorkplaceIds from '../utils/medic-desks';

interface Props {
  readonly floorPlan: FloorPlan;
  readonly unavailableWorkplaceIds: string[];
  readonly roomWorkplaceIds: string[];
  readonly selectedWorkplaceId: string | undefined;
  readonly onWorkplaceClick: (featureId: string) => void;
  /**
   * Desks that are out of service for the days being booked, and so are not drawn at all.
   *
   * Distinct from `unavailableWorkplaceIds`, which renders as booked — that would say a colleague
   * has the seat. A retired desk has no seat to have.
   */
  readonly hiddenWorkplaceIds?: readonly string[];
  /**
   * Who holds each taken desk, keyed by `featureId` — shown in a card while the pointer is on it.
   *
   * Optional, and absent for a caller that has no right to read it: a booked desk then behaves
   * exactly as it did before, as a coloured dot with a text title.
   */
  readonly occupantsByWorkplaceId?: WorkplaceOccupancyMap;
  /**
   * Whether those occupants carry contact details, as the server reported it.
   *
   * Passed through rather than inferred from the fields, so the card can tell "shared no number"
   * from "not being told the number".
   */
  readonly occupantsIncludeContactDetails?: boolean;
  /** How tall the surface is on screen. Width always fills the container. */
  readonly height?: number;
}

/**
 * The surface's own coordinate space, matching the editor's.
 *
 * Everything on a plan except the polygons is sized in these units and does not scale with
 * the plan: 14px label text, 24px icons, a 5px workplace dot. Drawn onto a 560×360 box
 * those were most of the picture — annotations collided with each other and buried the
 * desks underneath them. At the editor's size the same plan has room for them, and the
 * `viewBox` then scales the whole thing down to whatever the container actually is.
 */
const SURFACE_WIDTH = 1200;
const SURFACE_HEIGHT = 800;

const WORKPLACE_RADIUS = 5;

/**
 * A company medic's desk is the usual dot, a little larger, with a white plus knocked out of it.
 *
 * The dot keeps the colour every other desk is drawn in, so a medic's desk still reads as booked
 * like its neighbours; only the plus is added, inside the dot rather than hung off its side,
 * because anything beside a 5px dot is too small to make out at the zoom a plan opens at. The same
 * mark the Flutter app draws, so an administrator and an employee are shown one symbol.
 */
const MEDIC_RADIUS_FACTOR = 1.35;
/** Each arm's reach and the stroke, as shares of the medic dot's radius. */
const MEDIC_CROSS_ARM_FACTOR = 0.55;
const MEDIC_CROSS_THICKNESS_FACTOR = 0.3;
const MEDIC_CROSS_COLOR = StyleConstants.WHITE_COLOR;

/** How far a pointer may travel before a press counts as a pan rather than a workplace click. */
const DRAG_THRESHOLD_PIXELS = 4;

/**
 * How far the pointer moves within a desk before the occupant card is repositioned.
 *
 * Mouse-move-rate state updates re-render the whole surface. The card only has to move when the
 * room it has against the container edge changes, which a few pixels never does.
 */
const POINTER_MOVE_THRESHOLD_PIXELS = 12;

const AVAILABLE_COLOR = StyleConstants.LIGHT_GREEN_COLOR;
const BOOKED_COLOR = StyleConstants.PINK_COLOR;
const SELECTED_COLOR = StyleConstants.MEDIUM_BLUE_COLOR;
const OUT_OF_ROOM_COLOR = '#CCCCCC';

/**
 * Desk furniture, in the booking viewer's own colours.
 *
 * The editor draws these all but the same grey as the wall fragments around them, which
 * works there because hovering or selecting one lights it up. Nothing here is ever
 * selected, so without a colour of its own a desk drawing is indistinguishable from a wall
 * and the plan reads as an empty floor.
 */
const DESK_SHAPE_FILL = '#D8DEE9';
const DESK_SHAPE_STROKE = '#8A94A6';

/**
 * Pointer capture, where the environment has it.
 *
 * jsdom implements neither of these methods, so a drag under test would otherwise throw
 * before it reached the behaviour being tested.
 */
const capturePointer = (element: SVGSVGElement, pointerId: number): void => {
  if (typeof element.setPointerCapture !== 'function' || typeof element.hasPointerCapture !== 'function') {
    return;
  }

  if (!element.hasPointerCapture(pointerId)) {
    element.setPointerCapture(pointerId);
  }
};

const releasePointer = (element: SVGSVGElement, pointerId: number): void => {
  if (typeof element.releasePointerCapture !== 'function' || typeof element.hasPointerCapture !== 'function') {
    return;
  }

  if (element.hasPointerCapture(pointerId)) {
    element.releasePointerCapture(pointerId);
  }
};

/** Sits above the plan rather than on it, so the controls do not zoom along with it. */
const overlayButtonStyle = {
  backgroundColor: StyleConstants.WHITE_COLOR,
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  padding: 4,
} as const;

/**
 * The read-only floorplan an employee picks a workplace from.
 *
 * Draws the same five layers through the same components as the floorplan editor, so what
 * an office admin arranges is what a booking user sees. It previously drew only polygons
 * and workplace dots: desk furniture, text labels and icon annotations were all present in
 * the stored data and silently absent here.
 *
 * Pans and zooms like the editor, because a floorplan that fits on screen whole is rarely
 * one where individual desks are distinguishable.
 */
const FloorPlanViewer = ({
  floorPlan,
  unavailableWorkplaceIds,
  roomWorkplaceIds,
  selectedWorkplaceId,
  onWorkplaceClick,
  hiddenWorkplaceIds = [],
  occupantsByWorkplaceId,
  occupantsIncludeContactDetails = false,
  height = 520,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const viewport = useViewport(SURFACE_WIDTH, SURFACE_HEIGHT);
  const { viewBox } = viewport;

  const plan = useMemo(() => normalizeFloorPlan(floorPlan), [floorPlan]);

  const projection = useMemo(
    () => createProjection(computeBoundingBox(plan), SURFACE_WIDTH, SURFACE_HEIGHT),
    [plan],
  );

  // Only paid for when the plan actually carries icon annotations — most do not.
  const hasIcons = useMemo(() => plan.labels.some((label) => label.kind === 'iconLabel'), [plan]);
  // The result is not decoration: it is passed down to the annotations, which are memoised
  // and would otherwise never re-render once the set finally arrives.
  const iconsLoaded = useMdiIcons(hasIcons);

  const [hoveredWorkplaceId, setHoveredWorkplaceId] = useState<string | undefined>();
  const [panning, setPanning] = useState(false);

  // Where to open the occupant card, and how much room there is around it. Measured off the
  // container rather than off the SVG's own coordinates: the card is HTML sitting above the
  // plan, so it is positioned in CSS pixels and must not move when the plan is zoomed.
  const container = useRef<HTMLDivElement | null>(null);
  const [pointer, setPointer] = useState<
  { x: number; y: number; containerWidth: number; containerHeight: number } | undefined
  >();

  const trackPointer = useCallback((event: PointerEvent<SVGCircleElement>, force = false): void => {
    const bounds = container.current?.getBoundingClientRect();
    if (!bounds) return;

    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;

    // Every update re-renders the whole surface, and a plan carries hundreds of desks — so a
    // move is only worth a render once it could actually change which side of the pointer the
    // card opens on. The press-and-enter cases pass force, where the position is new by
    // definition.
    if (!force) {
      setPointer((previous) => {
        if (previous != null && Math.hypot(x - previous.x, y - previous.y) < POINTER_MOVE_THRESHOLD_PIXELS) {
          return previous;
        }
        return { x, y, containerWidth: bounds.width, containerHeight: bounds.height };
      });
      return;
    }

    setPointer({ x, y, containerWidth: bounds.width, containerHeight: bounds.height });
  }, []);

  // A press is ambiguous until the pointer moves: it becomes a pan, or it stays a click on
  // whatever was underneath. `moved` is what a workplace click checks before acting, so
  // dragging the plan by an area that happens to have a desk on it does not book it.
  const pan = useRef<{ clientX: number; clientY: number; moved: boolean } | undefined>();
  // Outlives the gesture, because the click that a workplace hears fires after the release
  // that ends the pan. Cleared by the next press rather than by the release for that reason.
  const dragged = useRef(false);

  // Deliberately no pointer capture here. Capturing on press retargets everything that
  // follows — including the release — at the surface, so a press on a workplace would never
  // reach the workplace. Capture is taken only once a press has become a pan, below.
  const onPointerDown = useCallback((event: PointerEvent<SVGSVGElement>): void => {
    pan.current = { clientX: event.clientX, clientY: event.clientY, moved: false };
    dragged.current = false;
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<SVGSVGElement>): void => {
      const origin = pan.current;
      if (!origin) return;

      const deltaX = event.clientX - origin.clientX;
      const deltaY = event.clientY - origin.clientY;

      if (!origin.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PIXELS) {
        return;
      }

      // Taken now rather than on the press: from here on this is a pan, and the pointer
      // needs to keep reaching the surface even once it leaves it.
      if (!origin.moved) {
        capturePointer(event.currentTarget, event.pointerId);
      }

      origin.moved = true;
      dragged.current = true;
      setPanning(true);

      // Screen pixels converted into surface units, so the plan tracks the pointer exactly
      // however far it is zoomed in.
      const bounds = event.currentTarget.getBoundingClientRect();
      viewport.panBy(
        -(deltaX / bounds.width) * viewBox.width,
        -(deltaY / bounds.height) * viewBox.height,
      );

      origin.clientX = event.clientX;
      origin.clientY = event.clientY;
    },
    [viewport, viewBox.width, viewBox.height],
  );

  const onPointerUp = useCallback((event: PointerEvent<SVGSVGElement>): void => {
    releasePointer(event.currentTarget, event.pointerId);
    setPanning(false);
    pan.current = undefined;
  }, []);

  const isOutsideRoom = useCallback(
    (featureId: string): boolean => roomWorkplaceIds.length > 0 && !roomWorkplaceIds.includes(featureId),
    [roomWorkplaceIds],
  );

  const getWorkplaceColor = useCallback(
    (featureId: string): string => {
      if (isOutsideRoom(featureId)) return OUT_OF_ROOM_COLOR;
      if (featureId === selectedWorkplaceId) return SELECTED_COLOR;
      if (unavailableWorkplaceIds.includes(featureId)) return BOOKED_COLOR;
      return AVAILABLE_COLOR;
    },
    [unavailableWorkplaceIds, isOutsideRoom, selectedWorkplaceId],
  );

  const getWorkplaceTitle = useCallback(
    (featureId: string): string => {
      if (isOutsideRoom(featureId)) return `${t('workplace')} ${featureId} (${t('unavailable')})`;
      if (unavailableWorkplaceIds.includes(featureId)) return `${t('workplace')} ${featureId} (${t('booked')})`;
      if (featureId === selectedWorkplaceId) return `${t('workplace')} ${featureId} (${t('selected')})`;
      return `${t('workplace')} ${featureId} (${t('available')})`;
    },
    [unavailableWorkplaceIds, isOutsideRoom, selectedWorkplaceId, t],
  );

  // Derived here rather than passed in, so both screens that draw this plan get the mark from
  // the occupants they already hand over.
  const medicDeskIds = useMemo(() => medicWorkplaceIds(occupantsByWorkplaceId), [occupantsByWorkplaceId]);

  // Gated on the plan's own idea of what is taken, for the same reason the occupant card is: the
  // occupants endpoint also reports walk-in check-ins, and a plus on a desk drawn green and still
  // bookable would say a first aider is sitting in a seat the plan is offering to somebody else.
  const isMedicDesk = useCallback(
    (featureId: string): boolean => medicDeskIds.has(featureId) && unavailableWorkplaceIds.includes(featureId),
    [medicDeskIds, unavailableWorkplaceIds],
  );

  const isClickable = useCallback(
    (featureId: string): boolean => !isOutsideRoom(featureId) && !unavailableWorkplaceIds.includes(featureId),
    [unavailableWorkplaceIds, isOutsideRoom],
  );

  // Only for the desk under the pointer, and never mid-pan: dragging the plan across a row of
  // booked desks would otherwise flash a card per desk it passed.
  //
  // Gated on the plan's own idea of what is taken, not only on the occupants map. The two are
  // read from different endpoints and the occupants one also reports walk-in check-ins, so
  // without this a desk drawn green and still bookable could hover as occupied — which is how an
  // administrator ends up either avoiding a free seat or double-booking a taken one.
  const hoveredOccupancy = !panning
    && hoveredWorkplaceId != null
    && unavailableWorkplaceIds.includes(hoveredWorkplaceId)
    ? occupantsByWorkplaceId?.get(hoveredWorkplaceId)
    : undefined;

  const hasOccupantCard = useCallback(
    (featureId: string): boolean =>
      unavailableWorkplaceIds.includes(featureId) && occupantsByWorkplaceId?.has(featureId) === true,
    [unavailableWorkplaceIds, occupantsByWorkplaceId],
  );

  const viewportControls = [
    {
      key: 'zoom-in',
      labelKey: 'floorplan-editor-zoom-in',
      icon: <AddIcon fontSize="small" />,
      onClick: viewport.zoomIn,
    },
    {
      key: 'zoom-out',
      labelKey: 'floorplan-editor-zoom-out',
      icon: <RemoveIcon fontSize="small" />,
      onClick: viewport.zoomOut,
    },
    {
      key: 'fit',
      labelKey: 'floorplan-editor-fit-to-view',
      icon: <CenterFocusStrongIcon fontSize="small" />,
      onClick: viewport.fitToView,
    },
  ];

  const legendRows = [
    { key: 'available', color: AVAILABLE_COLOR, outlined: false, text: t('available') },
    { key: 'booked', color: BOOKED_COLOR, outlined: false, text: t('booked') },
    { key: 'selected', color: SELECTED_COLOR, outlined: true, text: t('selected') },
    { key: 'unavailable', color: OUT_OF_ROOM_COLOR, outlined: false, text: t('unavailable') },
  ];

  // Explained only when there is something on the plan to explain: a legend row for a symbol
  // that appears nowhere is one more thing to read on every plan without a medic.
  const showsMedicLegend = plan.desks.some(
    (desk) => !hiddenWorkplaceIds.includes(desk.featureId) && isMedicDesk(desk.featureId),
  );

  return (
    <div
      ref={container}
      style={{
        position: 'relative',
        width: '100%',
        height,
        border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
        borderRadius: 8,
        backgroundColor: '#FAFAFA',
        overflow: 'hidden',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`${t('workplace-booking-floorplan-section-title')}`}
        style={{
          display: 'block',
          // Without this a touch drag scrolls the page instead of panning the plan.
          touchAction: 'none',
          cursor: panning ? 'grabbing' : 'grab',
        }}
        onWheel={(event: WheelEvent<SVGSVGElement>): void => viewport.zoomAt(event)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <LayerBackdrop floorPlan={plan} projection={projection} showFloor showRooms showBorder />

        {/* Desk furniture: the drawing a workplace dot sits on top of. */}
        <g style={{ pointerEvents: 'none' }}>
          {plan.deskShapes.map((shape) => (
            <DeskShape
              key={shape.id}
              shape={shape}
              projection={projection}
              fill={DESK_SHAPE_FILL}
              stroke={DESK_SHAPE_STROKE}
            />
          ))}
        </g>

        {/* Text and icon annotations — entrances, facilities, zone names. */}
        <g style={{ pointerEvents: 'none' }}>
          {plan.labels.map((label) => (
            <AnnotationLabel key={label.id} label={label} projection={projection} iconsLoaded={iconsLoaded} />
          ))}
        </g>

        {plan.desks
          // Filtered at the point of drawing. This viewer never saves a plan, but keeping the
          // filter here rather than in `normalizeFloorPlan` keeps the rule in one obvious place.
          .filter((desk) => !hiddenWorkplaceIds.includes(desk.featureId))
          .map((desk) => {
            const { x, y } = toScreen(desk.coordinates, projection);
            const { featureId } = desk;
            const clickable = isClickable(featureId);
            const selected = featureId === selectedWorkplaceId;
            const hovered = featureId === hoveredWorkplaceId;
            const medic = isMedicDesk(featureId);
            const radius = medic ? WORKPLACE_RADIUS * MEDIC_RADIUS_FACTOR : WORKPLACE_RADIUS;
            const title = medic
              ? `${getWorkplaceTitle(featureId)} - ${t('company-medic')}`
              : getWorkplaceTitle(featureId);

            return (
              <g key={desk.id} transform={`translate(${x}, ${y})`}>
                {/* Under the dot, so a rotated desk shows which way it faces, as in the editor. */}
                <g transform={`rotate(${desk.rotation})`} style={{ pointerEvents: 'none' }}>
                  <line
                    x1={0}
                    y1={0}
                    x2={0}
                    y2={-(WORKPLACE_RADIUS + 3)}
                    stroke={StyleConstants.GREY_COLOR}
                    strokeWidth={1}
                  />
                  {desk.deskType === 'pod' && (
                  <circle
                    r={WORKPLACE_RADIUS + 3}
                    fill="none"
                    stroke={StyleConstants.DARK_BLUE_COLOR}
                    strokeWidth={0.75}
                    strokeDasharray="2 2"
                  />
                  )}
                </g>

                <circle
                  r={hovered && !selected ? radius + 1.5 : radius}
                  fill={getWorkplaceColor(featureId)}
                  stroke={selected ? StyleConstants.DARK_BLUE_COLOR : StyleConstants.GREY_COLOR}
                  strokeWidth={selected || hovered ? 2 : 0.5}
                  style={{
                    cursor: clickable ? 'pointer' : 'not-allowed',
                    transition: 'fill 0.2s ease, stroke-width 0.2s ease',
                  }}
                // Selection happens on the release rather than on a click: once a pan takes
                // pointer capture the click is retargeted at the surface and never arrives
                // here at all. `dragged` is what keeps a pan that ends over a desk from
                // counting as a choice of that desk.
                  onPointerUp={(): void => {
                    if (clickable && !dragged.current) {
                      onWorkplaceClick(featureId);
                    }
                  }}
                  onPointerEnter={(event): void => {
                    setHoveredWorkplaceId(featureId);
                    trackPointer(event, true);
                  }}
                  // Tracked while the pointer is on the desk, not only on entry: the card opens
                  // on whichever side of the pointer has room, and a pointer that entered from
                  // the left and is now near the container edge needs the other side.
                  onPointerMove={(event): void => {
                    if (hasOccupantCard(featureId)) {
                      trackPointer(event);
                    }
                  }}
                  onPointerLeave={(): void => setHoveredWorkplaceId(undefined)}
                  aria-label={title}
                >
                  {/* Dropped while the occupant card is up, so the browser's own tooltip does
                      not appear on top of it saying less. The label above keeps the desk
                      readable to a screen reader either way. */}
                  {!(hovered && hasOccupantCard(featureId)) && (
                    <title>{title}</title>
                  )}
                </circle>

                {/* On top of the dot and deaf to the pointer, so the dot underneath still takes
                    the hover and the press exactly as an unmarked desk does. */}
                {medic && (
                  <g
                    data-testid={`medic-mark-${featureId}`}
                    style={{ pointerEvents: 'none' }}
                    stroke={MEDIC_CROSS_COLOR}
                    strokeWidth={radius * MEDIC_CROSS_THICKNESS_FACTOR}
                    strokeLinecap="round"
                  >
                    <line
                      x1={-radius * MEDIC_CROSS_ARM_FACTOR}
                      y1={0}
                      x2={radius * MEDIC_CROSS_ARM_FACTOR}
                      y2={0}
                    />
                    <line
                      x1={0}
                      y1={-radius * MEDIC_CROSS_ARM_FACTOR}
                      x2={0}
                      y2={radius * MEDIC_CROSS_ARM_FACTOR}
                    />
                  </g>
                )}
              </g>
            );
          })}
      </svg>

      {hoveredOccupancy && pointer && (
        <WorkplaceOccupantTooltip
          occupancy={hoveredOccupancy}
          containsContactDetails={occupantsIncludeContactDetails}
          x={pointer.x}
          y={pointer.y}
          containerWidth={pointer.containerWidth}
          containerHeight={pointer.containerHeight}
        />
      )}

      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {viewportControls.map((control) => (
          <IconButton
            key={control.key}
            size="small"
            style={overlayButtonStyle}
            aria-label={`${t(control.labelKey)}`}
            onClick={control.onClick}
          >
            {control.icon}
          </IconButton>
        ))}
      </div>

      {/* In HTML rather than in the SVG, so it keeps its size and place whatever the zoom. */}
      <div
        style={{
          position: 'absolute',
          left: 8,
          bottom: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 12px',
          padding: '6px 10px',
          borderRadius: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
          maxWidth: 'calc(100% - 16px)',
        }}
      >
        {legendRows.map((row) => (
          <span
            key={row.key}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: StyleConstants.TERTIARY_FONT_SIZE - 2,
              color: StyleConstants.GREY_COLOR,
              fontFamily: StyleConstants.HEADER_FONT_FAMILY,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: row.color,
                border: row.outlined ? `2px solid ${StyleConstants.DARK_BLUE_COLOR}` : 'none',
              }}
            />
            {row.text}
          </span>
        ))}
        {showsMedicLegend && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: StyleConstants.TERTIARY_FONT_SIZE - 2,
              color: StyleConstants.GREY_COLOR,
              fontFamily: StyleConstants.HEADER_FONT_FAMILY,
            }}
          >
            {/* The mark itself, drawn at legend size, rather than a description of it. */}
            <svg width={12} height={12} viewBox="-6 -6 12 12" aria-hidden="true">
              <circle r={6} fill={BOOKED_COLOR} />
              <g stroke={MEDIC_CROSS_COLOR} strokeWidth={6 * MEDIC_CROSS_THICKNESS_FACTOR} strokeLinecap="round">
                <line x1={-6 * MEDIC_CROSS_ARM_FACTOR} y1={0} x2={6 * MEDIC_CROSS_ARM_FACTOR} y2={0} />
                <line x1={0} y1={-6 * MEDIC_CROSS_ARM_FACTOR} x2={0} y2={6 * MEDIC_CROSS_ARM_FACTOR} />
              </g>
            </svg>
            {t('company-medic')}
          </span>
        )}
      </div>
    </div>
  );
};

export default FloorPlanViewer;
